import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Award, Users, Leaf, Phone, MessageCircle, ArrowRight,
  CheckCircle2, MapPin, Clock, Sparkles,
} from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import { Button } from '../components/ui/button';

const STATS = [
  { Icon: Users, value: '10,000+', label: 'Happy Customers' },
  { Icon: Award, value: '15+', label: 'Years of Experience' },
  { Icon: MapPin, value: '24', label: 'Cities Served' },
  { Icon: ShieldCheck, value: '99.4%', label: 'Customer Satisfaction' },
];

const VALUES = [
  {
    Icon: ShieldCheck,
    title: 'Certified Experts',
    desc: 'Government-licensed technicians trained in the latest IPM (Integrated Pest Management) techniques.',
  },
  {
    Icon: Leaf,
    title: 'Eco-Safe Solutions',
    desc: 'WHO/CIB-approved, biodegradable chemicals — safe for kids, pets, pregnant women and the environment.',
  },
  {
    Icon: Sparkles,
    title: 'Transparent Pricing',
    desc: 'Real-time online calculator with zero hidden charges. Pay only what you see.',
  },
  {
    Icon: Clock,
    title: 'On-Time Service',
    desc: 'Same-day appointments, punctual arrival, and complete service documentation after every visit.',
  },
];

export default function AboutMe({ onOpenCalculator }) {
  useSeo(
    'About EntoRid — India\'s Trusted Pest Control Experts',
    'Learn about EntoRid — Uttarakhand\'s most trusted pest control company. 15+ years of experience, 10,000+ happy customers, certified technicians, eco-friendly treatments.'
  );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">About Us</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 text-white">
        <div aria-hidden className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div aria-hidden className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-24">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 text-xs font-semibold">
            <Sparkles size={13} className="text-emerald-300" /> About EntoRid
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl" data-testid="aboutme-title">
            Built on Trust. Powered by Precision.
          </h1>
          <p className="mt-3 text-sm sm:text-lg text-white/85 max-w-2xl leading-relaxed">
            For over 15 years, EntoRid has protected homes, businesses and communities across Uttarakhand
            from pests — using safe, scientifically-proven methods and a customer-first approach.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 mb-12 sm:mb-16">
          {STATS.map(({ Icon, value, label }) => (
            <div key={label} className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm p-4 sm:p-5 text-center">
              <div className="inline-flex w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center mb-3">
                <Icon size={20} />
              </div>
              <div className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-none">{value}</div>
              <div className="text-[11px] sm:text-xs text-gray-500 mt-1.5 leading-tight">{label}</div>
            </div>
          ))}
        </section>

        {/* Our Story */}
        <section className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12 sm:mb-16 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Our Story</span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
              Pest control that puts your family first
            </h2>
            <p className="mt-4 text-sm sm:text-base text-gray-700 leading-relaxed">
              EntoRid was founded with a simple belief: every home and business deserves a pest-free environment
              without compromising safety or the planet. What started as a small team of three has grown into
              Uttarakhand's most trusted pest control company, serving thousands of homes across Dehradun,
              Haridwar, Rishikesh, Roorkee and beyond.
            </p>
            <p className="mt-3 text-sm sm:text-base text-gray-700 leading-relaxed">
              Today, we operate a fleet of trained specialists who arrive in uniform, follow documented IPM
              protocols, and stand behind every service with our free re-treatment warranty. Our commitment
              has never wavered: <strong className="text-slate-900">safe, effective, transparent service —
              every single time.</strong>
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={onOpenCalculator}
                data-testid="aboutme-book-btn"
                className="h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md"
              >
                Book a Free Inspection <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <Link to="/contact" className="h-12 px-6 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold inline-flex items-center gap-2">
                <Phone size={16} /> Contact Us
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=900&q=70&auto=format&fit=crop"
              alt="EntoRid technicians at work"
              loading="lazy"
              className="rounded-2xl shadow-xl ring-1 ring-gray-100 w-full h-auto object-cover"
            />
            <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-white text-slate-900 rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 ring-1 ring-gray-100">
              <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <ShieldCheck size={20} />
              </div>
              <div>
                <div className="text-base font-bold leading-none">Govt-Approved</div>
                <div className="text-[11px] text-gray-500 mt-1">CIB-licensed chemicals</div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="mb-12 sm:mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">What we stand for</span>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">Our Core Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {VALUES.map(({ Icon, title, desc }) => (
              <article key={title} className="bg-white rounded-2xl p-5 ring-1 ring-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 text-emerald-700 flex items-center justify-center ring-1 ring-slate-100">
                  <Icon size={22} />
                </div>
                <h3 className="mt-3 text-base font-bold text-slate-900">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Why us bullets */}
        <section className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-blue-50 ring-1 ring-emerald-100/50 p-6 sm:p-10 mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">Why Customers Choose EntoRid</h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-3">
            {[
              'Free on-site inspection before quoting',
              'Real-time transparent pricing — no hidden charges',
              'Eco-friendly chemicals — safe for kids, pets, plants',
              'Documented IPM protocols — audit-ready reports',
              'Free re-treatment if pests return within warranty',
              'Same-day & weekend slots available',
              '24/7 WhatsApp support',
              'Razorpay-secured online payments',
            ].map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-700 text-white p-6 sm:p-10 relative overflow-hidden">
          <div aria-hidden className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl font-bold leading-tight">Ready to live pest-free?</h2>
              <p className="mt-2 text-sm sm:text-base text-white/85">Get a free inspection and instant quote from Uttarakhand's most trusted pest experts.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:flex-shrink-0">
              <Button
                onClick={onOpenCalculator}
                data-testid="aboutme-cta-book"
                className="h-12 px-6 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-bold shadow-lg"
              >
                Book Free Inspection <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <a
                href="https://api.whatsapp.com/send?phone=919410110910"
                target="_blank" rel="noreferrer"
                data-testid="aboutme-cta-whatsapp"
                className="h-12 px-6 rounded-full bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2 font-bold transition"
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
